import React, { useEffect, useState } from 'react';
import { ordersService } from '@/shared/services/ordersService';
import { Order } from '@/shared/types/orders';
import { SettlementBatchType } from '@/shared/types/settlements';
import styles from './OrderDetailsModal.module.css';

interface OrderDetailsModalProps {
    orderIds: string[];
    batchType: SettlementBatchType;
    beneficiaryName: string;
    onClose: () => void;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ orderIds, batchType, beneficiaryName, onClose }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                // Fetch each order individually for now since we don't have a batch fetch endpoint
                const fetchedOrders = await Promise.all(
                    orderIds.map(id => ordersService.getOrder(id).then(res => res.order))
                );
                setOrders(fetchedOrders);
            } catch (error) {
                console.error('Error fetching orders for modal:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [orderIds]);

    if (loading) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modal}>
                    <div className={styles.loading}>Cargando detalles de pedidos...</div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>Desglose de Pedidos - {beneficiaryName}</h2>
                    <button className={styles.closeButton} onClick={onClose}>×</button>
                </div>

                <div className={styles.content}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Pedido</th>
                                <th>Productos</th>
                                <th>Subtotal</th>
                                <th>{batchType === 'RESTAURANT' ? 'Comisión' : 'Domicilio'}</th>
                                <th>Tipo/Pago</th>
                                <th>Neto Lote</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                let nettingAmount = 0;
                                let feeAmount = 0;

                                if (batchType === 'RESTAURANT') {
                                    // Lógica para Restaurantes
                                    const isPickupCash = order.payment.method === 'CASH_ON_DELIVERY' && order.type === 'pickup';
                                    feeAmount = order.financial?.platformFeeAmount || 0;
                                    nettingAmount = isPickupCash
                                        ? -feeAmount
                                        : (order.financial?.restaurantNetAmount || 0);
                                } else {
                                    // Lógica para Repartidores: Su ganancia es el cargo por domicilio
                                    feeAmount = order.financial?.courierDeliveryFee || 0;
                                    nettingAmount = order.financial?.courierDeliveryFee || 0;
                                }

                                return (
                                    <tr key={order.orderId}>
                                        <td>
                                            <strong>#{order.orderCode}</strong><br />
                                            <small>{order.createdAt?.seconds ? new Date(order.createdAt.seconds * 1000).toLocaleString() : 'N/A'}</small>
                                        </td>
                                        <td>
                                            <div className={styles.orderDetails}>
                                                {order.products.map((p, idx) => (
                                                    <div key={idx} className={styles.productItem}>
                                                        <span>{p.quantity}x {p.name}</span>
                                                        <span>${p.subtotal.toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                        <td>${order.financial?.subtotal.toFixed(2)}</td>
                                        <td className={batchType === 'RESTAURANT' ? styles.negative : styles.positive}>
                                            {batchType === 'RESTAURANT' ? '-' : ''}${feeAmount.toFixed(2)}
                                        </td>
                                        <td>
                                            <small
                                                style={{ whiteSpace: 'nowrap' }}
                                                title={
                                                    order.type === 'pickup' && order.payment.method === 'CASH_ON_DELIVERY'
                                                        ? 'Recogida en el local - Pago en efectivo'
                                                        : order.type === 'delivery' && order.payment.method === 'CASH_ON_DELIVERY'
                                                            ? 'Envío a domicilio - Pago en efectivo'
                                                            : order.type === 'pickup' && order.payment.method === 'VPAY_SMARTLINK'
                                                                ? 'Recogida en el local - Pago digital'
                                                                : order.type === 'delivery' && order.payment.method === 'VPAY_SMARTLINK'
                                                                    ? 'Envío a domicilio - Pago digital'
                                                                    : 'Tipo de pedido y pago'
                                                }>
                                                {order.type.toUpperCase()} {order.payment.method.replace(/_/g, ' ')}
                                            </small>
                                        </td>
                                        <td className={`${styles.amount} ${nettingAmount < 0 ? styles.negative : styles.positive}`}>
                                            ${nettingAmount.toFixed(2)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={styles.footer}>
                    <button className={styles.closeBtn} onClick={onClose}>Cerrar</button>
                </div>
            </div>
        </div>
    );
};
